/**
 * External Imports
 */
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuButton, NeuIcon, NeuList } from '@neutron/react';

/**
 * Internal Imports
 */
import SearchField from '../../../../components/searchHeader/searchField';

import {
  openModal,
  setModalType,
  editSelectedQuestion
} from '../../../../redux/actions';
import {
  usePostMasterTemplateMutation,
  useGetQuestionsQuery,
  usePostTemplateConfigMutation,
  useGetTemplateDocQuery
} from '../../../../redux/apis/Templates.api';

import { useUserRolesRef } from '../../../../services/UserRoles/userRoles';

import { date } from '../../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  ICondensedMasterTemplate,
  ICondensedQuestion,
  IFullTemplate,
  Question
} from '../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  EditTemplateView,
  HeaderText,
  QuestionBankQuestionCard,
  QuestionBankQuestionOverlayCard,
  QuestionBankView,
  QuestionCardHeader,
  QuestionNameLabel,
  QuestionNumberLabel,
  QuestionSideStrip,
  Tag,
  TagContainer,
  TemplateHeaderView,
  TemplateInfo,
  TemplateInfoContainer,
  TemplateQuestionCard,
  TemplateView,
  TitleLabel,
  UpDownStrip,
  UtilityBar
} from './EditMasterTemplate.styles';

interface EditMasterTemplateProps {
  authUser: AuthUserProps;
  selectedTemplateDocId: string;
  setSelectedMasterTemplate: Dispatch<
    SetStateAction<ICondensedMasterTemplate | IFullTemplate | null>
  >;
  setShowEditTemplateFlag: Dispatch<SetStateAction<boolean>>;
}

const EditMasterTemplate: FC<EditMasterTemplateProps> = ({
  authUser,
  selectedTemplateDocId,
  setSelectedMasterTemplate,
  setShowEditTemplateFlag
}) => {
  const dispatch = useDispatch();

  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(
    null
  );
  const [questionsOfTemplateState, setQuestionsOfTemplateState] = useState<
    Question[]
  >([]);
  const [questionBankFilteredQuestions, setQuestionBankFilteredQuestions] =
    useState<Question[]>([]);
  const [questionBankQuestionsToDisplay, setQuestionBankQuestionsToDisplay] =
    useState<Question[]>([]);

  const [searchTerm, setSearchTerm] = useState<string>('');

  const { isEnterpriseAdmin } = useUserRolesRef();

  const [
    postMasterTemplate
    // {
    //   isError: isPostMasterTemplateError,
    //   isLoading: isPostMasterTemplateLoading,
    //   isSuccess: isPostMasterTemplateSuccess
    // }
  ] = usePostMasterTemplateMutation();
  const {
    data: questionsData
    // isError: isQuestionsError,
    // isFetching: isQuestionsFetching,
    // isLoading: isQuestionsLoading
  } = useGetQuestionsQuery(undefined, {
    refetchOnMountOrArgChange: 300
  });
  const [
    postTemplateConfig
    // {
    //   isError: isPostTemplateConfigError,
    //   isLoading: isPostTemplateConfigLoading,
    //   isSuccess: isPostTemplateConfigSuccess
    // }
  ] = usePostTemplateConfigMutation();
  const {
    data: templateDocData
    //   isError: isGetTemplateDocError,
    //   isFetching: isGetTemplateDocFetching,
    //   isLoading: isGetTemplateDocLoading
  } = useGetTemplateDocQuery(selectedTemplateDocId, {
    refetchOnMountOrArgChange: true,
    skip: !selectedTemplateDocId
  });

  const handleAddQuestionToTemplate = (question: any) => {
    const tempQuestionsOfTemplateState = [...questionsOfTemplateState];
    tempQuestionsOfTemplateState.unshift(question);
    setQuestionsOfTemplateState(tempQuestionsOfTemplateState);
  };

  const handleOnFocus = (question: any) => {
    setSelectedQuestion(question);
  };

  const moveItem = (from: any, to: any) => {
    // remove `from` item and store it
    const tempQuestionsOfTemplate = [...questionsOfTemplateState];
    const f = tempQuestionsOfTemplate.splice(from, 1)[0];
    // insert stored item into position `to`
    tempQuestionsOfTemplate.splice(to, 0, f);
    setQuestionsOfTemplateState(tempQuestionsOfTemplate);
  };
  const moveUpDown = (selectedQuestionObject: any, updown: string) => {
    questionsOfTemplateState.every((question: any) => {
      if (question === selectedQuestionObject) {
        let to = null;
        if (updown === 'up') {
          if (questionsOfTemplateState.indexOf(question) !== 0) {
            to = questionsOfTemplateState.indexOf(question) - 1;
          }
        } else if (
          questionsOfTemplateState.length - 1 !==
          questionsOfTemplateState.indexOf(question)
        ) {
          to = questionsOfTemplateState.indexOf(question) + 1;
        }
        if (to !== null) {
          moveItem(questionsOfTemplateState.indexOf(question), to);
          return false;
        }
      }
      return question;
    });
  };

  const handleRemoveQuestion = (question: any) => {
    setQuestionsOfTemplateState(
      questionsOfTemplateState.filter((item: any) => item !== question)
    );
  };

  const handlePostTemplate = async (archivedFlag: any) => {
    const { archived } = archivedFlag;
    const questions: ICondensedQuestion[] = [];
    const conditions: { ask: string; flags: string[] }[] = [];

    questionsOfTemplateState.forEach((question: any) => {
      questions.push({
        id: question.questionId,
        templateLevel: templateDocData?.templateLevel ?? '',
        hidden: null
      });
      conditions.push({ ask: question.questionId, flags: [] });
    });

    try {
      const now = date.now();
      const nowUTC = new Date().toISOString();
      if (archived) {
        if (isEnterpriseAdmin) {
          await postMasterTemplate({
            ...templateDocData,
            userId: authUser.hcaid,
            archived,
            conditions,
            isPublished: true,
            questions,
            lastUpdated: nowUTC,
            lastUpdatedBy: authUser.hcaid,
            lastUpdatedByName: `${authUser.firstName} ${authUser.lastName}`,
            lastUpdatedDisplay: `${date.datePrettier(
              now
            )} ${date.localTimeOfDay(now)}`,
            lastUpdatedUTC: nowUTC
          });
          setSelectedMasterTemplate(null);
        } else console.log('Access denied !!');
      } else {
        const payload = {
          ...templateDocData,
          userId: authUser.hcaid,
          archived,
          conditions,
          isPublished: true,
          questions,
          lastUpdated: nowUTC,
          lastUpdatedBy: authUser.hcaid,
          lastUpdatedByName: `${authUser.firstName} ${authUser.lastName}`,
          lastUpdatedDisplay: `${date.datePrettier(now)} ${date.localTimeOfDay(
            now
          )}`,
          lastUpdatedUTC: nowUTC
        } as any;

        const [, , newTemplateLevel, newTemplateLevelId] =
          selectedTemplateDocId.split(':');
        if (newTemplateLevel === 'master') {
          await postMasterTemplate(payload);
        } else {
          delete payload.templateId;
          payload.templateLevel = newTemplateLevel;
          payload.templateLevelId = newTemplateLevelId;
          await postTemplateConfig(payload);
        }
      }
    } catch (error) {
      console.error('Error updating template config:', error);
    }
    setShowEditTemplateFlag(false);
  };

  const handleCancel = () => {
    setShowEditTemplateFlag(false);
    // appInsights.trackEvent({
    //   name: 'Master_Template_Canceled'
    // });
  };

  const editQuestionOnClick = (id?: string) => {
    if (id) dispatch(editSelectedQuestion(id));
    dispatch(setModalType('Add Question'));
    dispatch(openModal());
  };

  const sortTags = (inputArray: string[]) => {
    return [...inputArray].sort((a, b) => {
      // const numA = Number(a);
      // const numB = Number(b);

      if (!Number.isNaN(+a) && !Number.isNaN(+b)) {
        return +a - +b;
      }

      if (!Number.isNaN(+a)) {
        return -1;
      }

      if (!Number.isNaN(+b)) {
        return 1;
      }

      return a.toLowerCase().localeCompare(b.toLowerCase());
    });
  };

  useEffect(() => {
    if (templateDocData && questionsData && questionsData.length >= 0) {
      setQuestionsOfTemplateState(
        templateDocData.questions?.reduce(
          (acc: Question[], ques: ICondensedQuestion) => {
            const foundQuestionInBank = questionsData.find(
              (q: Question) => q.questionId === ques.id
            );
            return foundQuestionInBank
              ? [...acc, { ...ques, ...foundQuestionInBank }]
              : acc;
          },
          [] as Question[]
        ) || ([] as Question[])
      );
    }
  }, [templateDocData, questionsData]);

  useEffect(() => {
    if (templateDocData && questionsData) {
      const filteredArray = questionsData.filter(
        (value: any) =>
          !questionsOfTemplateState.includes(value) &&
          value.roundingType === templateDocData.roundingType
      );
      setQuestionBankFilteredQuestions(filteredArray);
    }
  }, [templateDocData, questionsData, questionsOfTemplateState]);

  useEffect(() => {
    const q = new RegExp(searchTerm.trim().replace(/\\/g, ''), 'i');
    const filtered = questionBankFilteredQuestions.filter((question: any) =>
      `${question.questionTitle}`.match(q)
    );
    setQuestionBankQuestionsToDisplay(filtered);
  }, [searchTerm, questionBankFilteredQuestions]);

  // TODO: Refactor the rendering logic below for EditMasterTemplate.
  return (
    <EditTemplateView>
      <div style={{ maxWidth: '32em', flex: '0 0 40%' }}>
        <TemplateView>
          <div className="d-flex flex-column mb-2">
            <TemplateHeaderView>
              <div className="d-flex justify-content-between">
                <HeaderText>Template</HeaderText>
                <QuestionNumberLabel>
                  {questionsOfTemplateState?.length} Questions
                </QuestionNumberLabel>
              </div>
              <TemplateInfoContainer>
                <div className="d-flex justify-content-between w-100">
                  <TitleLabel>
                    Master Template
                    <TemplateInfo>{templateDocData?.name || ''}</TemplateInfo>
                  </TitleLabel>
                  <TitleLabel style={{ marginLeft: 15 }}>
                    Level
                    <TemplateInfo>
                      {templateDocData?.templateLevel || ''}
                    </TemplateInfo>
                  </TitleLabel>
                  <TitleLabel style={{ marginLeft: 15 }}>
                    Level ID
                    <TemplateInfo>
                      {templateDocData?.templateLevelId || ''}
                    </TemplateInfo>
                  </TitleLabel>
                </div>
              </TemplateInfoContainer>
            </TemplateHeaderView>
          </div>
          <div style={{ maxHeight: `calc(100vh - 450px)`, overflow: 'scroll' }}>
            <NeuList data-testid="template-questions" unit="group">
              {questionsOfTemplateState.length > 0 &&
                questionsOfTemplateState?.map((question: any) => {
                  return (
                    <NeuList key={question?.questionId} interactive unit="item">
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          height: 'auto'
                        }}
                      >
                        <UpDownStrip>
                          <NeuIcon
                            color="gray"
                            size="1x"
                            style={{ marginTop: 10, marginRight: 3 }}
                            onClick={() => moveUpDown(question, 'up')}
                          >
                            arrow_upward
                          </NeuIcon>
                          <NeuIcon
                            color="gray"
                            size="1x"
                            style={{ marginRight: 3 }}
                          >
                            drag_indicator
                          </NeuIcon>
                          <NeuIcon
                            color="gray"
                            size="1x"
                            style={{ marginRight: 3 }}
                            onClick={() => moveUpDown(question, 'down')}
                          >
                            arrow_downward
                          </NeuIcon>
                        </UpDownStrip>
                        <TemplateQuestionCard>
                          <QuestionCardHeader>
                            <div className="d-flex justify-content-start align-items-center">
                              <div
                                className="neu-text-blue-80"
                                style={{
                                  fontWeight: 700,
                                  fontSize: 14,
                                  marginTop: 0.5,
                                  marginRight: 10
                                }}
                              >
                                {questionsOfTemplateState.indexOf(question) + 1}
                              </div>
                              <QuestionNameLabel
                                className="text-clamp text-clamp__2"
                                color="blue-60"
                                aria-multiline
                              >
                                {question?.questionTitle}
                              </QuestionNameLabel>{' '}
                            </div>
                            <div
                              className="d-flex justify-content-end"
                              style={{ flex: '0 0 30%' }}
                            >
                              {(question.questionTags?.includes('LOCKED') &&
                                isEnterpriseAdmin) ||
                              !question.questionTags?.includes('LOCKED') ? (
                                <NeuButton
                                  color="blue"
                                  kind="tertiary"
                                  size="small"
                                  style={{
                                    marginLeft: 326,
                                    marginTop: -15,
                                    position: 'absolute'
                                  }}
                                  onClick={() => handleRemoveQuestion(question)}
                                >
                                  <NeuIcon size="1x" style={{ marginRight: 3 }}>
                                    cancel
                                  </NeuIcon>
                                  Remove
                                </NeuButton>
                              ) : (
                                ''
                              )}
                            </div>
                          </QuestionCardHeader>
                          <NeuList className="neu-bg-white" unit="group">
                            {question?.questionParts?.map(
                              (questionPart: any) => {
                                return (
                                  <NeuList
                                    key={questionPart.questionPartId}
                                    className="w-100 p-1"
                                    unit="item"
                                    // TODO: Need to fix style in this component
                                    style={{
                                      backgroundColor: 'white',
                                      fontWeight: 500,
                                      fontSize: 12,
                                      lineHeight: '20px',
                                      color: '#4B4B4B'
                                    }}
                                  >
                                    {questionPart.question}
                                  </NeuList>
                                );
                              }
                            )}
                          </NeuList>
                          <TagContainer>
                            {sortTags(question?.questionTags).map(
                              (tag: string) => (
                                <Tag key={tag}>{`${tag}`}</Tag>
                              )
                            )}
                          </TagContainer>
                        </TemplateQuestionCard>
                      </div>
                    </NeuList>
                  );
                })}
            </NeuList>
          </div>
        </TemplateView>
        <UtilityBar>
          {isEnterpriseAdmin && (
            <NeuButton
              className="mt-1"
              kind="tertiary"
              size="small"
              style={{
                color: 'white !important'
              }}
              onClick={() => handlePostTemplate({ archived: true })}
            >
              <NeuIcon color="plain-0">delete</NeuIcon>
              Remove
            </NeuButton>
          )}
          <div className="mx-1">
            <NeuButton
              className="mt-1"
              kind="tertiary"
              size="small"
              style={{
                color: 'white !important',
                marginLeft: 246
              }}
              onClick={() => handleCancel()}
            >
              Cancel
            </NeuButton>
            <NeuButton
              className="mt-1"
              color="blue"
              kind="primary"
              size="small"
              style={{
                marginLeft: 340
              }}
              onClick={() => handlePostTemplate({ archived: false })}
            >
              Update
            </NeuButton>
          </div>
        </UtilityBar>
      </div>

      <QuestionBankView>
        <div className="d-flex justify-content-between align-items-center p-2">
          <HeaderText>Question Bank</HeaderText>
          <div
            className="d-flex"
            style={{
              height: '24px',
              marginTop: 10
            }}
          >
            {isEnterpriseAdmin && (
              <NeuButton
                icon="add"
                size="small"
                onClick={() => editQuestionOnClick()}
              >
                Add Question
              </NeuButton>
            )}
          </div>
        </div>
        <SearchField searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
        <NeuList
          data-testid="bank-questions"
          className="neu-bg-white"
          unit="group"
          style={{
            maxHeight: '100%',
            marginTop: 10,
            overflowX: 'hidden',
            overflowY: 'auto'
          }}
        >
          {questionBankQuestionsToDisplay.length > 0 &&
            questionBankQuestionsToDisplay.map((question: any) => {
              return (
                <NeuList
                  key={question?.questionId}
                  interactive
                  unit="item"
                  onMouseEnter={() => handleOnFocus(question)}
                >
                  <div
                    className="d-flex h-auto"
                    style={{
                      minHeight: 101
                    }}
                  >
                    <QuestionSideStrip>
                      <NeuIcon
                        color="gray-30"
                        size="1x"
                        style={{ marginRight: 3, marginTop: 35 }}
                      >
                        drag_indicator
                      </NeuIcon>
                    </QuestionSideStrip>
                    <div className="w-100 h-auto" style={{ minHeight: 101 }}>
                      <QuestionBankQuestionCard style={{ minHeight: 101 }}>
                        {selectedQuestion === question && (
                          <QuestionBankQuestionOverlayCard
                            style={{
                              alignItems: 'center',
                              justifyContent: 'space-around',
                              backgroundColor: 'rgba(173,216,230,0.90)'
                            }}
                          >
                            <NeuButton
                              color="blue"
                              kind="tertiary"
                              size="small"
                              onClick={() =>
                                handleAddQuestionToTemplate(question)
                              }
                            >
                              Add Question to Template
                            </NeuButton>
                            {isEnterpriseAdmin && (
                              <NeuButton
                                color="blue"
                                kind="tertiary"
                                size="small"
                                onClick={() =>
                                  editQuestionOnClick(question.questionId)
                                }
                              >
                                Edit Question
                              </NeuButton>
                            )}
                          </QuestionBankQuestionOverlayCard>
                        )}
                        <div className="d-flex flex-column">
                          <QuestionNameLabel
                            color="blue-60"
                            style={{ padding: 16 }}
                          >
                            {question?.questionTitle}
                          </QuestionNameLabel>
                          <NeuList className="neu-bg-white" unit="group">
                            {question?.questionParts?.map(
                              (questionPart: any) => (
                                <NeuList
                                  key={questionPart.questionPartId}
                                  className="w-100 p-1"
                                  unit="item"
                                  // TODO: Need to fix style in this component
                                  style={{
                                    overflow: 'hidden !important',
                                    backgroundColor: 'white',
                                    fontWeight: 500,
                                    fontSize: 12,
                                    lineHeight: '20px',
                                    color: '#4B4B4B',
                                    paddingBottom: 10
                                  }}
                                >
                                  {questionPart.question}
                                </NeuList>
                              )
                            )}
                          </NeuList>
                        </div>
                        <TagContainer>
                          {sortTags(question?.questionTags).map(
                            (tag: string) => (
                              <Tag key={tag}>{`${tag}`}</Tag>
                            )
                          )}
                        </TagContainer>
                      </QuestionBankQuestionCard>
                    </div>
                  </div>
                </NeuList>
              );
            })}
        </NeuList>
      </QuestionBankView>
    </EditTemplateView>
  );
};

const mapReduxStateToProps = (state: any) => {
  return {
    authUser: state.AuthorizedUser.authorizedUser
  };
};
export default connect(mapReduxStateToProps)(EditMasterTemplate);
